import React, { useState } from 'react';
import { Calendar, Plus, Trash2, Play, Pause } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { updateExportSchedule, deleteExportSchedule } from '../../../services/erpIntegrationService';

const ExportSchedulePanel = ({ schedules, integrations, onRefresh }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await deleteExportSchedule(scheduleId);
      onRefresh();
    } catch (error) {
      alert(`Failed to delete schedule: ${error?.message}`);
    }
  };

  const handleToggleSchedule = async (schedule) => {
    try {
      await updateExportSchedule(schedule?.id, {
        status: schedule?.status === 'active' ? 'paused' : 'active'
      });
      onRefresh();
    } catch (error) {
      alert(`Failed to update schedule: ${error?.message}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Export Schedules</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automated data exports to your ERP systems
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Schedule
        </Button>
      </div>
      <div className="p-6">
        {schedules?.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No export schedules configured</p>
            <Button
              onClick={() => setShowAddModal(true)}
              variant="outline"
            >
              Create Your First Schedule
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules?.map((schedule) => {
              const integration = integrations?.find(i => i?.id === schedule?.integration_id);
              return (
                <div
                  key={schedule?.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900">{schedule?.name}</h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            schedule?.status === 'active' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {schedule?.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">ERP System:</span>{' '}
                          {integration?.erp_system_name || 'Unknown'}
                        </div>
                        <div>
                          <span className="font-medium">Data Type:</span>{' '}
                          {schedule?.data_type}
                        </div>
                        <div>
                          <span className="font-medium">Frequency:</span>{' '}
                          {schedule?.frequency}
                        </div>
                        <div>
                          <span className="font-medium">Format:</span>{' '}
                          {schedule?.export_format}
                        </div>
                        <div>
                          <span className="font-medium">Next Run:</span>{' '}
                          {schedule?.next_run_at
                            ? new Date(schedule.next_run_at)?.toLocaleString()
                            : 'Not scheduled'}
                        </div>
                        <div>
                          <span className="font-medium">Last Run:</span>{' '}
                          {schedule?.last_run_at
                            ? new Date(schedule.last_run_at)?.toLocaleString()
                            : 'Never'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        onClick={() => handleToggleSchedule(schedule)}
                        variant="outline"
                        size="sm"
                      >
                        {schedule?.status === 'active' ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeleteSchedule(schedule?.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportSchedulePanel;